import { ProductTileMediumProps, NotificationType } from '@pb/components'
import { LoadingStates } from '@pb/utils'

import { FieldConfig } from 'App/duck/types'

export enum PaymentMode {
  Card = 'Card',
  COD = 'CD',
}

export type OrderStatus = 'DELIVERED' | 'SHIPPED'

export interface ReturnsState {
  labelsStatus: LoadingStates
  orderId: string
  email: string
  returnsLoginFormStatus: LoadingStates
  orderItemsStatus: LoadingStates
  consignments: Consignment[]
  customerInfo: CustomerInfo
  orderInfo: OrderInfo
  paymentMode?: PaymentMode
  paymentCardProvider?: string
  paymentCardDigits?: string
  currency: string
  submittedReturnsInfo?: SubmittedReturnsInfo
  submitReturnsInfoStatus: LoadingStates
  notification?: Notification
  selectedItemForEdit?: NormalizedOrderItem
  pickupDate?: string
  returnsReasons?: ReasonCode[]
  returnsReasonsStatus: LoadingStates
  isAuthenticated: boolean
  orderStatus: OrderStatus
}

export interface SubmitReturnData {
  customer: CustomerInfo
  pickupDate?: string
  itemsForReturn: SubmitReturnsItem[] | boolean
}

export interface SubmittedReturnsInfo {
  returnCreated: boolean
  digitalReturnId: string
  externalReturnReference: string
  errorMessage?: string
}

export interface Consignment {
  consignmentId: string
  warehouse: string
  returnableItems: NormalizedOrderItem[]
  nonReturnableItems: NormalizedOrderItem[]
  returnedItems: NormalizedOrderItem[]
}
export interface CustomerInfo {
  email: string
  phone: string
  customerBpid: string
  address: CustomerAddress
  returnsPickupAddress?: ReturnsPickupAddress
}

interface CustomerAddress {
  firstName: string
  lastName: string
  line1: string
  line2: string
  city: string
  postalCode: string
  province?: string
  countryIsoCode: string
  country: string
}

export type ReturnsPickupAddress = Pick<
  CustomerAddress,
  'firstName' | 'lastName' | 'line1' | 'line2' | 'city' | 'postalCode' | 'province'
> & {
  isValid?: boolean
}

export interface OrderInfo {
  createdAt: string
  invoiceNumber?: string
}

export interface ReturnsLoginState {
  orderId: string
  email: string
}

export interface LoginFormData {
  email: string
  orderNumber: string
}

export interface LoginData {
  orderId: string
  email: string
  country: string | undefined
  lang: string
}

export interface NormalizedOrderItem {
  data: OrderItemData
  tableData: ProductTileMediumProps['tableData']
  isSelected: boolean
  selectedQuantityValue?: number
  returnsInfo?: ReturnsData
  badgeText?: string
}

export enum ReturnsType {
  RETURN = 'return',
  EXCHANGE = 'exchange',
}

type ReturnsData =
  | {
      returnType: ReturnsType
      returnReasonCode?: string
      returnReasonDesc?: string
      comments?: string
    }
  | {
      returnType?: never
      returnReasonCode?: never
      returnReasonDesc?: never
      comments?: never
    }

export type OrderItemData = ProductTileMediumProps['data'] & {
  image: string
  imageAltText: string
  productId: string
  quantity: number
  paidPrice: number
  currency: string
  itemId: string
  productCode: string
  consignmentId: string
  warehouse: string
  color?: string
  size?: string
}

export interface OrderItemsResponse {
  data: {
    consignments: Consignment[]
    customer: CustomerInfo
    createdAt: string
    paymentMode: PaymentMode
    paymentCardDigits?: string
    paymentCardProvider?: string
    orderId: string
    currency: string
    invoiceNumber?: string
    status: string
  }
  status: number
}

type TotalData = {
  name: string
  value: string
}

export interface ReturnTotal {
  tableData: TotalData[]
  totalData: TotalData
}

export interface RefundPrices {
  selectedItemQuantityPrice: number[]
  totalRefundPrice: number
}

export interface ReasonCode {
  code: string
  description: string
}

export interface SubmitReturnsItem {
  lineItemId: string
  productCode: string
  consignmentId: string
  returnReasonCode?: string
  returnReasonDesc?: string
  comments?: string
  returnQty?: number
  quantityShipped: number
  compensationMethod: string
}

export interface SubmitReturnsInfo {
  submitReturnData: SubmitReturnData
  orderId: string
  lang: string
  country: string | undefined
}

export interface ValidateReturnsOrderResponse {
  data: { login?: boolean; message?: string; errorMessage: string; sessionId: string }
  status: number
}
export interface SubmitReturnsResponse {
  data: SubmittedReturnsInfo
  status: number
}
export interface Notification {
  message: string
  type: NotificationType['type']
}
export interface SetItemAsReturn {
  productId: string
  itemId: string
  returnReasonCode?: string
  returnReasonDesc?: string
  comments?: string
  quantity: number
}
export interface OrderItemsSuccess {
  consignments: Consignment
  orderDate: string
  customerInfo: object
  paymentMode: PaymentMode
  paymentCardDigits?: string
  paymentCardProvider?: string
  orderId: string
  currency: string
  invoiceNumber?: string
  orderStatus: OrderStatus
}
export interface UnsetItemFromReturn {
  itemId: string
  productId: string
}

export interface SetQuantityForReturnItem {
  itemId: string
  productId: string
  selectedQuantityValue: number
}

export interface FetchReturnsReasonsQueryParams {
  country?: string
  lang: string
}

export interface FetchOrderItemsQueryParams {
  orderId: string
  country?: string
  lang: string
}

export type Labels = {
  [key: string]: string
}
export interface FetchInitialDataQueryParams {
  lang: string
  country?: string
}
export interface FetchPickupDatesQueryParams {
  country?: string
  orderStatus: OrderStatus
}

export enum Form {
  Login = 'loginFields',
  PickupAddress = 'pickupAddressFields',
  ReturnsReason = 'returnsReasonFields',
}

export type FieldsConfigData = {
  [key in Form]?: [FieldConfig]
}

export type MarketConfigData = {
  carrierPortalURL?: string
}

export enum ErrorTypes {
  FIELD = 'field',
  SYSTEM = 'system',
}

export interface TrackSubmittedProductData {
  productArticleId: string
  productSizeCode: string
  selectedQuantityValue?: number
  returnReasonCode: string
  returnReasonDesc: string
  comments: string
}

export interface UtagDataReturnsConfirmation {
  customerBpid: string
  digitalReturnId: string
  returnsOrderId: string
  currency: string
  productArticleIds: string[]
  productSizeCodes: string[]
  quantities: number[]
  selectedItemQuantityPrice: number[]
  totalRefundPrice: number
  getItemsSelectedForReturn: TrackSubmittedProductData
}
